import { useState } from 'react'
import { MoreVertOutlined } from '@mui/icons-material'
import { ContextMenuIcon, ContextMenuItem } from 'features/common/ui/ContextMenu'
import { useIntlMessages } from 'app/localization/useIntlMessages'
import { confirmWithDialog } from 'features/common/ui/ConfirmDialog/askConfirmation'
import { ConfirmDialogResult } from 'features/common/ui/ConfirmDialog/ConfirmDialog'
import { CommentAction } from 'features/collaboration/types'

const messageIds = {
  copyLink: 'collaboration.comments.actions.copyLink',
  markAsResolved: 'collaboration.comments.actions.markAsResolved',
  deleteThread: 'collaboration.comments.actions.deleteThread',
  deleteComment: 'collaboration.comments.actions.deleteComment',
  reopen: 'collaboration.comments.actions.reopen',
  moreActions: 'collaboration.comments.actions.moreActions',
  edit: 'collaboration.comments.actions.edit',

  deleteThreadConfirmTitle: 'collaboration.comments.deleteThreadConfirm.title',
  deleteThreadConfirmMessage: 'collaboration.comments.deleteThreadConfirm.message',
  deleteCommentConfirmTitle: 'collaboration.comments.deleteCommentConfirm.title',
  deleteCommentConfirmMessage: 'collaboration.comments.deleteCommentConfirm.message',
  yesDelete: 'common.button.yesDelete'
}

function CommentActionsMenu({
  isThreadRoot = false,
  onClick,
  canDelete = false,
  canResolve,
  resolved,
  canEdit = true
}: {
  isThreadRoot?: boolean
  resolved?: boolean
  onClick: (action: CommentAction) => void
  canDelete?: boolean
  canResolve?: boolean
  canEdit?: boolean
}) {
  canResolve = canResolve ?? isThreadRoot
  const [isOpen, setIsOpen] = useState(false)
  const messages = useIntlMessages(messageIds)
  const handleClick = async (action: CommentAction) => {
    if (action === 'delete') {
      const title = isThreadRoot ? messages.deleteThreadConfirmTitle : messages.deleteCommentConfirmTitle
      const message = isThreadRoot ? messages.deleteThreadConfirmMessage : messages.deleteCommentConfirmMessage
      const confirmation = await confirmWithDialog({
        title,
        message,
        confirmLabel: messages.yesDelete,
        confirmButtonProps: { color: 'error', variant: 'text' }
      })
      if (confirmation !== ConfirmDialogResult.Confirm) return

      onClick(action)
    } else {
      onClick(action)
    }
  }
  return (
    <ContextMenuIcon
      aria-label={messages.moreActions}
      isOpen={setIsOpen}
      onClickItem={(item) => handleClick(item.id as CommentAction)}
      menuItems={
        [
          isThreadRoot && {
            id: 'copyLink',
            label: messages.copyLink
          },
          canResolve && {
            id: 'toggleResolved',
            label: resolved ? messages.reopen : messages.markAsResolved
          },
          canEdit && {
            id: 'edit',
            label: messages.edit
          },
          canDelete && {
            divider: isThreadRoot || canResolve || canEdit,
            id: 'delete',
            destructive: true,
            label: isThreadRoot ? messages.deleteThread : messages.deleteComment
          }
        ].filter(Boolean) as ContextMenuItem[]
      }
      tooltip={!isOpen && messages.moreActions}
    >
      <MoreVertOutlined />
    </ContextMenuIcon>
  )
}

export default CommentActionsMenu
